'use client'

import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  ClockIcon,
  EyeIcon,
  EyeOffIcon,
  HashIcon,
  ImageIcon,
  MailIcon,
  MapPinIcon,
  PackageIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  ReceiptIcon,
  RefreshCwIcon,
  SaveIcon,
  ShoppingCartIcon,
  StickyNoteIcon,
  Trash2Icon,
  UserIcon,
  XIcon,
  CopyIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { NumberInput } from '@/components/ui/number-input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { ContentError, DeleteConfirmationDialog, UnsavedChangesGuardDialog } from '@/components/partials'
import {
  useAddOrderNote,
  useDeleteOrder,
  useDeleteOrderNote,
  useOrder,
  useUnsavedChangesGuard,
  useUpdateOrder,
  useUpdateOrderNote,
  useUpdateOrderStatus,
  useCopyToClipboard
} from '@/hooks'
import { OrderStatusBadge } from '@/components/partials/orders'
import { AddOrderItemDialog, OrderItemEntry } from '@/components/partials/orders/add-order-item-dialog'
import OrderExportDialog from '@/components/partials/orders/order-export-dialog'
import StickyBottomSaveBar from '@/components/partials/sticky-bottom-save-bar'
import ContentDetailInfo from '@/components/partials/content-detail-info'
import { formatDateTime, formatMoney, getResizedPictureUrl } from '@/lib/utils'
import { OrderItemType, OrderNoteType } from '@/types/order'
import { OrderStatusEnum } from '@/enums'
import { useMetaStore } from '@/store'

type PropsType = { id: string }

type PendingItemEdit = {
  qty: number
  price: number | undefined
  discountRate: number | undefined
  taxRate: number | undefined
  priceIncludesTax: boolean | undefined
}

// Compute gross price, tax, discount and line totals for an item given pending edits
const calcItemDisplay = (
  item: OrderItemType,
  edit: PendingItemEdit | undefined
): {
  unitOriginalDisplay: number // original price for display (net for pit=false, gross for pit=true)
  unitOriginalGross: number // original gross (for reference)
  unitGross: number
  unitNet: number
  discountRate: number
  discountAmount: number
  lineTaxAmount: number
  lineTotal: number
} => {
  const qty = edit?.qty ?? item.quantity
  const taxRate = edit?.taxRate ?? item.taxRate ?? 0
  const pit = edit?.priceIncludesTax !== undefined ? edit.priceIncludesTax : (item.priceIncludesTax ?? true)
  const effectiveDiscountRate = edit?.discountRate ?? item.unitPrice?.discountRate ?? 0

  // For pit=true: discount base = gross. For pit=false: discount base = net.
  // edit.price stores the same convention (gross for pit=true, net for pit=false).
  const originalBase =
    edit?.price !== undefined
      ? edit.price
      : pit
        ? (item.unitPrice?.originalGross ?? item.unitPrice?.gross ?? 0)
        : (item.unitPrice?.originalNet ?? item.unitPrice?.net ?? 0)

  const discountedEntered = effectiveDiscountRate > 0 ? originalBase * (1 - effectiveDiscountRate / 100) : originalBase
  const discountAmount = originalBase - discountedEntered

  let unitGross: number
  let unitNet: number
  if (pit) {
    unitGross = discountedEntered
    unitNet = taxRate > 0 ? (unitGross * 100) / (100 + taxRate) : unitGross
  } else {
    unitNet = discountedEntered
    unitGross = taxRate > 0 ? unitNet * (1 + taxRate / 100) : unitNet
  }

  const unitOriginalGross = pit ? originalBase : taxRate > 0 ? originalBase * (1 + taxRate / 100) : originalBase

  return {
    unitOriginalDisplay: originalBase,
    unitOriginalGross,
    unitGross,
    unitNet,
    discountRate: effectiveDiscountRate,
    discountAmount,
    lineTaxAmount: (unitGross - unitNet) * qty,
    lineTotal: unitGross * qty
  }
}

const calcNewItemDisplay = (
  ni: OrderItemEntry
): {
  unitOriginalGross: number
  unitGross: number
  unitNet: number
  lineTaxAmount: number
  lineTotal: number
  discountRate: number
  discountAmount: number
} => {
  const originalGross = ni.originalGross ?? ni.unitPriceOverride ?? 0
  const discountRate = ni.discountRateOverride ?? 0
  const discountedEntered = discountRate > 0 ? originalGross * (1 - discountRate / 100) : (ni.unitPriceOverride ?? 0)
  const taxRate = ni.taxRate ?? 0
  let unitGross: number
  let unitNet: number
  if (ni.priceIncludesTax !== false) {
    unitGross = discountedEntered
    unitNet = taxRate > 0 ? (unitGross * 100) / (100 + taxRate) : unitGross
  } else {
    unitNet = discountedEntered
    unitGross = taxRate > 0 ? unitNet * (1 + taxRate / 100) : unitNet
  }
  const unitTax = unitGross - unitNet
  const discountAmount = discountRate > 0 ? originalGross - discountedEntered : 0
  return {
    unitOriginalGross: originalGross,
    unitGross,
    unitNet,
    lineTaxAmount: unitTax * ni.quantity,
    lineTotal: unitGross * ni.quantity,
    discountRate,
    discountAmount
  }
}

const OrderDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="h-9 w-16" />
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
    </div>
    {Array.from({ length: 3 }).map((_, i) => (
      <Card key={i}>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-32" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const { metaState } = useMetaStore()
  const activeLocales = metaState.response?.locales.filter(l => l.active) ?? []

  const [selectedStatus, setSelectedStatus] = useState<OrderStatusEnum | null>(null)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [notifyCustomer, setNotifyCustomer] = useState(false)
  const [pendingDialogShowPrices, setPendingDialogShowPrices] = useState<boolean | undefined>(undefined)
  const [pendingDialogLocale, setPendingDialogLocale] = useState<string | undefined>(undefined)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const [pendingItemEdits, setPendingItemEdits] = useState<Record<string, PendingItemEdit>>({})
  const [pendingNewItems, setPendingNewItems] = useState<OrderItemEntry[]>([])
  const [pendingDeleteItemIds, setPendingDeleteItemIds] = useState<Set<string>>(new Set())
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [editingNewIdx, setEditingNewIdx] = useState<number | null>(null)
  const [isSavingAll, setIsSavingAll] = useState(false)

  const [editingDiscount, setEditingDiscount] = useState(false)
  const [pendingDiscountRate, setPendingDiscountRate] = useState<number | undefined>(undefined)

  const [showAddItem, setShowAddItem] = useState(false)

  const [noteContent, setNoteContent] = useState('')
  const [noteIsPublic, setNoteIsPublic] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editNoteContent, setEditNoteContent] = useState('')
  const [editNoteIsPublic, setEditNoteIsPublic] = useState(false)
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null)

  const { data: order, isLoading, error } = useOrder(id)
  const { mutate: updateOrder } = useUpdateOrder(id)
  const { mutate: updateStatus, isPending: isUpdating } = useUpdateOrderStatus()
  const { mutate: deleteOrder, isPending: isDeleting } = useDeleteOrder()
  const { mutate: addNote, isPending: isAddingNote } = useAddOrderNote(id)
  const { mutate: updateNote, isPending: isUpdatingNote } = useUpdateOrderNote(id)
  const { mutate: deleteNote, isPending: isDeletingNote } = useDeleteOrderNote(id)

  // ── Preview totals ──────────────────────────────────────────────────────────
  const previewTotals = useMemo(() => {
    if (!order) {
      return null
    }
    const discountRate = pendingDiscountRate ?? order.customer.discountRate

    let subtotal = 0
    let taxTotal = 0

    // Existing items with pending edits (skip deleted)
    for (const item of order.items) {
      if (pendingDeleteItemIds.has(item.id)) {
        continue
      }
      const edit = pendingItemEdits[item.id]
      const { lineTotal, lineTaxAmount } = calcItemDisplay(item, edit)
      subtotal += lineTotal
      taxTotal += lineTaxAmount
    }

    // Pending new items
    for (const ni of pendingNewItems) {
      const { lineTotal, lineTaxAmount } = calcNewItemDisplay(ni)
      subtotal += lineTotal
      taxTotal += lineTaxAmount
    }

    const discountAmount = subtotal * (discountRate / 100)
    const total = subtotal - discountAmount

    return { subtotal, discountAmount, total, taxTotal, discountRate }
  }, [order, pendingItemEdits, pendingNewItems, pendingDiscountRate, pendingDeleteItemIds])

  const isDirty = useMemo(
    () =>
      Object.keys(pendingItemEdits).length > 0 ||
      pendingNewItems.length > 0 ||
      pendingDeleteItemIds.size > 0 ||
      (pendingDiscountRate !== undefined && !!order && pendingDiscountRate !== order.customer.discountRate),
    [pendingItemEdits, pendingNewItems, pendingDeleteItemIds, pendingDiscountRate, order]
  )

  // Block browser refresh / tab close, and client-side navigation when there are unsaved changes
  const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(isDirty)

  // ESC key cancels active item/discount editing
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return
      }
      if (editingItemId) {
        e.preventDefault()
        const item = order?.items.find(i => i.id === editingItemId)
        if (item) {
          const current = pendingItemEdits[editingItemId]
          const pit =
            current?.priceIncludesTax !== undefined ? current.priceIncludesTax : (item.priceIncludesTax ?? true)
          const origRef = pit
            ? (item.unitPrice?.originalGross ?? item.unitPrice?.gross ?? undefined)
            : (item.unitPrice?.originalNet ?? item.unitPrice?.net ?? undefined)
          if (
            current &&
            current.qty === item.quantity &&
            current.price === origRef &&
            current.priceIncludesTax === (item.priceIncludesTax ?? undefined)
          ) {
            setPendingItemEdits(prev => {
              const next = { ...prev }
              delete next[editingItemId]
              return next
            })
          }
        }
        setEditingItemId(null)
      } else if (editingDiscount) {
        e.preventDefault()
        setEditingDiscount(false)
        setPendingDiscountRate(undefined)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [editingItemId, editingDiscount, order, pendingItemEdits])

  if (isLoading) {
    return <OrderDetailSkeleton />
  }
  if (error || !order || !previewTotals) {
    return <ContentError error={error} listUrl="/orders" />
  }

  const effectiveDiscountRate = pendingDiscountRate ?? order.customer.discountRate

  const startEditItem = (item: OrderItemType) => {
    setEditingItemId(item.id)
    if (!pendingItemEdits[item.id]) {
      const pit = item.priceIncludesTax ?? true
      const originalRef = pit
        ? (item.unitPrice?.originalGross ?? item.unitPrice?.gross ?? undefined)
        : (item.unitPrice?.originalNet ?? item.unitPrice?.net ?? undefined)
      setPendingItemEdits(prev => ({
        ...prev,
        [item.id]: {
          qty: item.quantity,
          price: originalRef,
          discountRate: item.unitPrice?.discountRate ?? undefined,
          taxRate: item.taxRate ?? undefined,
          priceIncludesTax: item.priceIncludesTax ?? undefined
        }
      }))
    }
  }

  const cancelEditItem = () => {
    if (editingItemId) {
      const item = order.items.find(i => i.id === editingItemId)
      if (item) {
        const current = pendingItemEdits[editingItemId]
        const pit = item.priceIncludesTax ?? true
        const origRef = pit
          ? (item.unitPrice?.originalGross ?? item.unitPrice?.gross ?? undefined)
          : (item.unitPrice?.originalNet ?? item.unitPrice?.net ?? undefined)
        if (
          current &&
          current.qty === item.quantity &&
          current.price === origRef &&
          current.discountRate === (item.unitPrice?.discountRate ?? undefined) &&
          current.taxRate === (item.taxRate ?? undefined) &&
          current.priceIncludesTax === (item.priceIncludesTax ?? undefined)
        ) {
          const next = { ...pendingItemEdits }
          delete next[editingItemId]
          setPendingItemEdits(next)
        }
      }
    }
    setEditingItemId(null)
  }

  const handleSaveAll = async () => {
    setIsSavingAll(true)
    setEditingItemId(null)
    setEditingDiscount(false)
    try {
      const itemUpdates = Object.entries(pendingItemEdits)
        .filter(([itemId, edit]) => {
          const original = order.items.find(i => i.id === itemId)
          const pit = original?.priceIncludesTax ?? true
          const origRef = pit
            ? (original?.unitPrice?.originalGross ?? original?.unitPrice?.gross ?? undefined)
            : (original?.unitPrice?.originalNet ?? original?.unitPrice?.net ?? undefined)
          return (
            edit.qty !== original?.quantity ||
            edit.price !== origRef ||
            edit.discountRate !== (original?.unitPrice?.discountRate ?? undefined) ||
            edit.taxRate !== (original?.taxRate ?? undefined) ||
            edit.priceIncludesTax !== (original?.priceIncludesTax ?? undefined)
          )
        })
        .map(([itemId, edit]) => {
          const discountedPrice =
            edit.price !== undefined && edit.discountRate && edit.discountRate > 0
              ? edit.price * (1 - edit.discountRate / 100)
              : edit.price
          return {
            id: itemId,
            quantity: edit.qty,
            unitPriceOverride: discountedPrice,
            discountRateOverride: edit.discountRate,
            taxRate: edit.taxRate,
            priceIncludesTax: edit.priceIncludesTax
          }
        })

      const newItemsPayload = pendingNewItems.map(ni => ({
        productId: ni.productId,
        freeProductCode: !ni.productId ? ni.productCode : undefined,
        freeProductName: !ni.productId ? ni.productTitle : undefined,
        quantity: ni.quantity,
        // Only send unitPriceOverride if admin explicitly changed the price (same logic as create).
        unitPriceOverride: ni.productId && !ni.priceManuallyOverridden ? undefined : ni.unitPriceOverride,
        discountRateOverride: ni.productId ? ni.discountRateOverride : undefined,
        taxRate: ni.taxRate,
        priceIncludesTax: ni.priceIncludesTax
      }))

      const hasDiscountChange = pendingDiscountRate !== undefined && pendingDiscountRate !== order.customer.discountRate

      await new Promise<void>((resolve, reject) => {
        updateOrder(
          {
            discountRate: hasDiscountChange ? pendingDiscountRate : undefined,
            items: itemUpdates.length > 0 ? itemUpdates : undefined,
            newItems: newItemsPayload.length > 0 ? newItemsPayload : undefined,
            deletedItemIds: pendingDeleteItemIds.size > 0 ? Array.from(pendingDeleteItemIds) : undefined
          },
          { onSuccess: () => resolve(), onError: reject }
        )
      })

      await queryClient.invalidateQueries({ queryKey: ['order', id] })
      setPendingItemEdits({})
      setPendingNewItems([])
      setPendingDeleteItemIds(new Set())
      setPendingDiscountRate(undefined)
    } finally {
      setIsSavingAll(false)
    }
  }

  const handleDiscardAll = () => {
    setPendingItemEdits({})
    setPendingNewItems([])
    setPendingDeleteItemIds(new Set())
    setPendingDiscountRate(undefined)
    setEditingDiscount(false)
    setEditingItemId(null)
  }

  const handleAddItems = (items: OrderItemEntry[]) => {
    setPendingNewItems(prev => [...prev, ...items])
    setShowAddItem(false)
  }

  const openStatusDialog = () => {
    setSelectedStatus(null)
    setNotifyCustomer(false)
    setPendingDialogShowPrices(undefined)
    setPendingDialogLocale(order.locale ?? '')
    setStatusDialogOpen(true)
  }

  const handleStatusUpdate = () => {
    const statusChanged = selectedStatus !== null && selectedStatus !== order.status
    const showPricesChanged = pendingDialogShowPrices !== undefined && pendingDialogShowPrices !== order.showPrices
    const localeChanged = (pendingDialogLocale || '') !== (order.locale ?? '')
    if (!statusChanged && !showPricesChanged && !localeChanged) {
      return
    }
    updateStatus(
      {
        id,
        params: {
          status: selectedStatus ?? order.status,
          notifyCustomer,
          showPrices: showPricesChanged ? pendingDialogShowPrices : undefined,
          locale: localeChanged ? pendingDialogLocale || undefined : undefined
        }
      },
      {
        onSuccess: () => {
          setSelectedStatus(null)
          setNotifyCustomer(false)
          setPendingDialogShowPrices(undefined)
          setPendingDialogLocale(undefined)
          setStatusDialogOpen(false)
        }
      }
    )
  }

  const confirmDelete = () => {
    deleteOrder(id, { onSuccess: () => router.push('/orders') })
  }

  const handleAddNote = () => {
    if (!noteContent.trim()) {
      return
    }
    addNote(
      { content: noteContent, isPublic: noteIsPublic },
      {
        onSuccess: () => {
          setNoteContent('')
          setNoteIsPublic(false)
        }
      }
    )
  }

  const startEditNote = (note: OrderNoteType) => {
    setEditingNoteId(note.id)
    setEditNoteContent(note.content)
    setEditNoteIsPublic(note.isPublic)
  }

  const saveEditNote = () => {
    if (!editingNoteId) {
      return
    }
    updateNote(
      { noteId: editingNoteId, params: { content: editNoteContent, isPublic: editNoteIsPublic } },
      { onSuccess: () => setEditingNoteId(null) }
    )
  }

  const confirmDeleteNote = () => {
    if (!deleteNoteId) {
      return
    }
    deleteNote(deleteNoteId, { onSuccess: () => setDeleteNoteId(null) })
  }

  const showPreviewTotals = isDirty

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href="/orders">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('back')}</span>
              </Link>
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold">
                <ShoppingCartIcon className="h-6 w-6" />
                {t('order')}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground font-mono text-sm">{order.id}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(order.id, t('copied'))}
                  className="h-6 w-6 p-0">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            <OrderExportDialog orderId={id} />
            <Button onClick={openStatusDialog} size="sm" variant="outline">
              <RefreshCwIcon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('update_order_status')}</span>
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteConfirmOpen(true)}>
              <Trash2Icon className="h-4 w-4" />
              <span className="hidden lg:inline">{t('delete')}</span>
            </Button>
          </div>
        </div>

        {/* Pending changes bar — removed from here, now fixed at bottom */}

        {/* General Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ReceiptIcon className="h-4 w-4" />
              {t('general_information')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <HashIcon className="h-3.5 w-3.5" />
                  {t('order_number')}
                </p>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-sm">{order.id}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(order.id, t('copied'))}
                    className="h-6 w-6 p-0">
                    <CopyIcon className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('order_status')}</p>
                <OrderStatusBadge status={order.status} />
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('customer_type')}</p>
                <p className="font-medium">{t(`customer_type_${order.customerType}` as any)}</p>
              </div>
              {order.customerType === 'guest' && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('show_prices_to_customer')}</p>
                  <p className="font-medium">{order.showPrices ? t('yes') : t('no')}</p>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('language')}</p>
                <p className="font-medium">
                  {order.locale
                    ? (activeLocales.find(l => l.code === order.locale)?.name ?? activeLocales[0]?.name ?? order.locale)
                    : '—'}
                </p>
              </div>
            </div>
            <Separator className="my-4" />
            <ContentDetailInfo entity={order} />
          </CardContent>
        </Card>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 text-base">
                <span className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  {t('customer_info')}
                </span>
                {order.customer.userId && (
                  <Button variant="outline" size="sm" asChild>
                    <Link href={`/users/${order.customer.userId}`}>
                      <EyeIcon className="h-4 w-4" />
                      {t('view_profile')}
                    </Link>
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <UserIcon className="h-3.5 w-3.5" />
                  {t('full_name')}
                </p>
                <p className="font-medium">
                  {order.customer.firstname} {order.customer.lastname}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <MailIcon className="h-3.5 w-3.5" />
                  {t('email')}
                </p>
                <p className="text-sm">{order.customer.email}</p>
              </div>
              {order.customer.phone && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <PhoneIcon className="h-3.5 w-3.5" />
                    {t('phone')}
                  </p>
                  <p className="text-sm">{order.customer.phone}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Shipping Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPinIcon className="h-4 w-4" />
                {t('shipping_address')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('country')}</p>
                <p className="font-medium">{order.shippingAddress.country}</p>
              </div>
              {order.shippingAddress.state && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('state')}</p>
                  <p className="text-sm">{order.shippingAddress.state}</p>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('city')}</p>
                <p className="text-sm">{order.shippingAddress.city}</p>
              </div>
              {order.shippingAddress.zipCode && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('zip_code')}</p>
                  <p className="text-sm">{order.shippingAddress.zipCode}</p>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('address')}</p>
                <p className="text-sm">{order.shippingAddress.addressText}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <PackageIcon className="h-4 w-4" />
              {t('order_items')}
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowAddItem(true)}>
              <PlusIcon className="h-4 w-4" />
              {t('add_item')}
            </Button>
          </CardHeader>
          <CardContent>
            <AddOrderItemDialog
              open={showAddItem}
              onOpenChange={setShowAddItem}
              currencySymbol={order.currencySymbol}
              currencyCode={order.currencyCode}
              onAddItems={handleAddItems}
            />
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24 text-center">{t('picture')}</TableHead>
                    <TableHead className="min-w-48">{t('product')}</TableHead>
                    <TableHead className="text-center">{t('quantity')}</TableHead>
                    <TableHead className="text-right">{t('unit_price')}</TableHead>
                    <TableHead className="text-right">{t('discount_rate')}</TableHead>
                    <TableHead className="text-right">{t('discounted_price')}</TableHead>
                    <TableHead className="text-right">{t('price_without_tax')}</TableHead>
                    <TableHead className="text-right">{t('price_with_tax')}</TableHead>
                    <TableHead className="text-right">{t('tax_rate')}</TableHead>
                    <TableHead className="text-center">{t('price_includes_tax')}</TableHead>
                    <TableHead className="text-right">{t('subtotal')}</TableHead>
                    <TableHead className="w-24 text-center">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Existing items */}
                  {order.items.map(item => {
                    const isPendingDelete = pendingDeleteItemIds.has(item.id)
                    const isEditing = editingItemId === item.id && !isPendingDelete
                    const edit = pendingItemEdits[item.id]
                    const {
                      unitOriginalDisplay,
                      unitGross,
                      unitNet,
                      discountRate: effectiveDiscountRate,
                      discountAmount,
                      lineTotal
                    } = calcItemDisplay(item, edit)
                    const hasPending = !!edit
                    const qtyChanged = hasPending && edit.qty !== item.quantity
                    const pit =
                      edit?.priceIncludesTax !== undefined ? edit.priceIncludesTax : (item.priceIncludesTax ?? true)
                    const origRef = pit
                      ? (item.unitPrice?.originalGross ?? item.unitPrice?.gross ?? undefined)
                      : (item.unitPrice?.originalNet ?? item.unitPrice?.net ?? undefined)
                    const priceChanged =
                      hasPending &&
                      (edit.price !== origRef || edit.priceIncludesTax !== (item.priceIncludesTax ?? undefined))
                    const discountChanged =
                      hasPending && edit.discountRate !== (item.unitPrice?.discountRate ?? undefined)
                    const taxRateChanged = hasPending && edit.taxRate !== (item.taxRate ?? undefined)

                    return (
                      <TableRow
                        key={item.id}
                        className={
                          isPendingDelete
                            ? 'bg-red-50/50 opacity-50 dark:bg-red-950/20'
                            : hasPending
                              ? 'bg-amber-50/50 dark:bg-amber-950/20'
                              : ''
                        }
                        onClick={() =>
                          !isEditing && !isPendingDelete && item.product && router.push(`/products/${item.product.id}`)
                        }>
                        <TableCell
                          className={`text-center ${item.product && !isEditing && !isPendingDelete ? 'cursor-pointer' : ''}`}
                          onClick={e => {
                            e.stopPropagation()
                            if (item.product && !isEditing && !isPendingDelete) {
                              router.push(`/products/${item.product.id}`)
                            }
                          }}>
                          {item.product?.mainPicture ? (
                            <Image
                              src={getResizedPictureUrl(item.product.mainPicture.path, 80, 60)}
                              alt={item.product.mainPicture.name || item.productName}
                              width={80}
                              height={60}
                              className={`mx-auto rounded object-cover ${isPendingDelete ? 'grayscale' : ''}`}
                            />
                          ) : (
                            <div className="bg-muted mx-auto flex h-15 w-20 items-center justify-center rounded">
                              <ImageIcon className="text-muted-foreground h-6 w-6" />
                            </div>
                          )}
                        </TableCell>
                        {/* Product code + name */}
                        <TableCell
                          className={`font-medium ${item.product && !isEditing && !isPendingDelete ? 'cursor-pointer' : ''}`}
                          onClick={e => {
                            e.stopPropagation()
                            if (item.product && !isEditing && !isPendingDelete) {
                              router.push(`/products/${item.product.id}`)
                            }
                          }}>
                          <p
                            className={`text-muted-foreground font-mono text-xs ${isPendingDelete ? 'line-through' : ''}`}>
                            {item.productCode}
                          </p>
                          <p className={isPendingDelete ? 'text-muted-foreground line-through' : ''}>
                            {item.product
                              ? (item.product.translations[locale] ??
                                item.product.translations['en-US'] ??
                                Object.values(item.product.translations)[0])
                              : item.productName}
                          </p>
                          {item.product && !item.product.active && (
                            <span className="text-xs text-red-500">({t('inactive')})</span>
                          )}
                        </TableCell>

                        {/* Quantity */}
                        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                          {isEditing ? (
                            <div className="flex justify-center">
                              <div className="w-20">
                                <NumberInput
                                  min={1}
                                  stepper={1}
                                  value={edit?.qty}
                                  onValueChange={v =>
                                    setPendingItemEdits(prev => ({
                                      ...prev,
                                      [item.id]: { ...prev[item.id], qty: v ?? 1 }
                                    }))
                                  }
                                  onKeyDown={e => e.key === 'Enter' && cancelEditItem()}
                                  inputClassName="text-right"
                                />
                              </div>
                            </div>
                          ) : (
                            <span
                              className={
                                isPendingDelete
                                  ? 'text-muted-foreground line-through'
                                  : qtyChanged
                                    ? 'font-semibold text-amber-600'
                                    : ''
                              }>
                              {edit?.qty ?? item.quantity}
                            </span>
                          )}
                        </TableCell>

                        {/* Unit Price (original before discount) */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {isEditing ? (
                            <div className="flex items-center justify-end gap-1">
                              {order.currencySymbol && (
                                <span className="text-muted-foreground shrink-0 text-sm">{order.currencySymbol}</span>
                              )}
                              <div className="w-28 shrink-0">
                                <NumberInput
                                  min={0}
                                  stepper={0.5}
                                  decimalScale={2}
                                  fixedDecimalScale
                                  value={edit?.price ?? unitOriginalDisplay}
                                  onValueChange={v =>
                                    setPendingItemEdits(prev => ({
                                      ...prev,
                                      [item.id]: { ...prev[item.id], price: v }
                                    }))
                                  }
                                  onKeyDown={e => e.key === 'Enter' && cancelEditItem()}
                                  placeholder="0.00"
                                  inputClassName="text-right"
                                />
                              </div>
                            </div>
                          ) : (
                            <span
                              className={`font-medium ${priceChanged ? 'text-amber-600' : isPendingDelete ? 'text-muted-foreground line-through' : ''}`}>
                              {formatMoney(
                                {
                                  amount: unitOriginalDisplay,
                                  currencyCode: order.currencyCode,
                                  currencySymbol: order.currencySymbol
                                },
                                locale
                              )}
                            </span>
                          )}
                        </TableCell>

                        {/* Discount Rate */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {isEditing ? (
                            <div className="ml-auto w-24">
                              <NumberInput
                                min={0}
                                max={100}
                                stepper={0.5}
                                decimalScale={2}
                                suffix="%"
                                value={edit?.discountRate}
                                onValueChange={v =>
                                  setPendingItemEdits(prev => ({
                                    ...prev,
                                    [item.id]: { ...prev[item.id], discountRate: v }
                                  }))
                                }
                                onKeyDown={e => e.key === 'Enter' && cancelEditItem()}
                                placeholder="0"
                                inputClassName="text-right"
                              />
                            </div>
                          ) : (
                            <span className={`text-xs ${discountChanged ? 'font-semibold text-amber-600' : ''}`}>
                              {effectiveDiscountRate > 0 ? (
                                <Badge variant="outline" className="text-xs">
                                  %{effectiveDiscountRate}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </span>
                          )}
                        </TableCell>

                        {/* Discounted Price */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className={isPendingDelete ? 'text-muted-foreground line-through' : ''}>
                            <div className={`font-medium ${priceChanged || discountChanged ? 'text-amber-600' : ''}`}>
                              {formatMoney(
                                {
                                  amount: unitGross,
                                  currencyCode: order.currencyCode,
                                  currencySymbol: order.currencySymbol
                                },
                                locale
                              )}
                            </div>
                            {discountAmount > 0 && (
                              <div className="text-xs text-green-600">
                                -
                                {formatMoney(
                                  {
                                    amount: discountAmount,
                                    currencyCode: order.currencyCode,
                                    currencySymbol: order.currencySymbol
                                  },
                                  locale
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>

                        {/* Price without Tax (net) */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <span className={isPendingDelete ? 'text-muted-foreground line-through' : ''}>
                            {formatMoney(
                              {
                                amount: unitNet,
                                currencyCode: order.currencyCode,
                                currencySymbol: order.currencySymbol
                              },
                              locale
                            )}
                          </span>
                        </TableCell>

                        {/* Price with Tax (gross) */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <span className={isPendingDelete ? 'text-muted-foreground line-through' : ''}>
                            {formatMoney(
                              {
                                amount: unitGross,
                                currencyCode: order.currencyCode,
                                currencySymbol: order.currencySymbol
                              },
                              locale
                            )}
                          </span>
                        </TableCell>

                        {/* Tax Rate */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {isEditing ? (
                            <div className="ml-auto w-24">
                              <NumberInput
                                min={0}
                                max={100}
                                stepper={0.5}
                                decimalScale={2}
                                suffix="%"
                                value={edit?.taxRate}
                                onValueChange={v =>
                                  setPendingItemEdits(prev => ({
                                    ...prev,
                                    [item.id]: { ...prev[item.id], taxRate: v }
                                  }))
                                }
                                onKeyDown={e => e.key === 'Enter' && cancelEditItem()}
                                placeholder="0"
                                inputClassName="text-right"
                              />
                            </div>
                          ) : (
                            <span className={taxRateChanged ? 'font-semibold text-amber-600' : ''}>
                              {(edit?.taxRate ?? item.taxRate ?? 0) > 0 ? (
                                <Badge variant="outline" className="text-xs">
                                  %{edit?.taxRate ?? item.taxRate}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </span>
                          )}
                        </TableCell>

                        {/* Price includes tax badge / switch */}
                        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                          {isEditing ? (
                            <Switch
                              checked={edit?.priceIncludesTax ?? item.priceIncludesTax ?? true}
                              onCheckedChange={checked => {
                                const current = pendingItemEdits[item.id] ?? {
                                  qty: item.quantity,
                                  price: item.priceIncludesTax
                                    ? (item.unitPrice?.originalGross ?? item.unitPrice?.gross ?? undefined)
                                    : (item.unitPrice?.originalNet ?? item.unitPrice?.net ?? undefined),
                                  discountRate: item.unitPrice?.discountRate ?? undefined,
                                  taxRate: item.taxRate ?? undefined,
                                  priceIncludesTax: item.priceIncludesTax ?? undefined
                                }
                                const taxRate = current.taxRate ?? 0
                                let newPrice = current.price
                                if (newPrice !== undefined && taxRate > 0) {
                                  if (checked) {
                                    // excl → incl: net → gross
                                    newPrice = Math.round(newPrice * (1 + taxRate / 100) * 10000) / 10000
                                  } else {
                                    // incl → excl: gross → net
                                    newPrice = Math.round((newPrice / (1 + taxRate / 100)) * 10000) / 10000
                                  }
                                }
                                setPendingItemEdits(prev => ({
                                  ...prev,
                                  [item.id]: { ...current, price: newPrice, priceIncludesTax: checked }
                                }))
                              }}
                            />
                          ) : item.priceIncludesTax !== null ? (
                            <Badge variant={item.priceIncludesTax ? 'default' : 'secondary'} className="text-xs">
                              {item.priceIncludesTax ? t('incl_tax') : t('excl_tax')}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Subtotal */}
                        <TableCell className="text-right font-medium" onClick={e => e.stopPropagation()}>
                          <span
                            className={
                              isPendingDelete
                                ? 'text-muted-foreground line-through'
                                : hasPending
                                  ? 'text-amber-600'
                                  : ''
                            }>
                            {formatMoney(
                              {
                                amount: lineTotal,
                                currencyCode: order.currencyCode,
                                currencySymbol: order.currencySymbol
                              },
                              locale
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                          {isPendingDelete ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-green-600"
                              title={t('undo')}
                              onClick={() =>
                                setPendingDeleteItemIds(prev => {
                                  const next = new Set(prev)
                                  next.delete(item.id)
                                  return next
                                })
                              }>
                              <XIcon className="h-3.5 w-3.5" />
                            </Button>
                          ) : isEditing ? (
                            <div className="flex justify-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                title={t('save')}
                                onClick={cancelEditItem}>
                                <SaveIcon className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => {
                                  const next = { ...pendingItemEdits }
                                  delete next[item.id]
                                  setPendingItemEdits(next)
                                  setEditingItemId(null)
                                }}>
                                <XIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => startEditItem(item)}>
                                <PencilIcon className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive h-7 w-7"
                                onClick={() => {
                                  setPendingDeleteItemIds(prev => new Set(prev).add(item.id))
                                  if (editingItemId === item.id) {
                                    setEditingItemId(null)
                                  }
                                }}>
                                <Trash2Icon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}

                  {/* Pending new items */}
                  {pendingNewItems.map((ni, idx) => {
                    const {
                      unitOriginalGross: niOriginalGross,
                      unitGross,
                      unitNet: niUnitNet,
                      lineTotal,
                      discountRate: niDiscountRate,
                      discountAmount: niDiscountAmount
                    } = calcNewItemDisplay(ni)
                    const isEditingNew = editingNewIdx === idx
                    return (
                      <TableRow key={`new-${idx}`} className="bg-green-50/50 dark:bg-green-950/20">
                        <TableCell className="text-center">
                          <div className="bg-muted mx-auto flex h-15 w-20 items-center justify-center rounded">
                            <ImageIcon className="text-muted-foreground h-6 w-6" />
                          </div>
                        </TableCell>
                        {/* Product code + name */}
                        <TableCell className="font-medium">
                          <p className="text-muted-foreground font-mono text-xs">{ni.productCode}</p>
                          <p>
                            {ni.productTitle}
                            <Badge variant="outline" className="ml-1 text-xs text-green-600">
                              {t('new')}
                            </Badge>
                          </p>
                        </TableCell>

                        {/* Quantity */}
                        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                          {isEditingNew ? (
                            <div className="flex justify-center">
                              <div className="w-20">
                                <NumberInput
                                  min={1}
                                  stepper={1}
                                  value={ni.quantity}
                                  onValueChange={v =>
                                    setPendingNewItems(prev =>
                                      prev.map((item, i) => (i === idx ? { ...item, quantity: v ?? 1 } : item))
                                    )
                                  }
                                  inputClassName="text-right"
                                />
                              </div>
                            </div>
                          ) : (
                            <span>{ni.quantity}</span>
                          )}
                        </TableCell>

                        {/* Unit Price (original) */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {isEditingNew ? (
                            <div className="flex items-center justify-end gap-1">
                              {order.currencySymbol && (
                                <span className="text-muted-foreground shrink-0 text-sm">{order.currencySymbol}</span>
                              )}
                              <div className="w-28 shrink-0">
                                <NumberInput
                                  min={0}
                                  stepper={0.5}
                                  decimalScale={2}
                                  fixedDecimalScale
                                  value={ni.unitPriceOverride}
                                  onValueChange={v =>
                                    setPendingNewItems(prev =>
                                      prev.map((item, i) => (i === idx ? { ...item, unitPriceOverride: v } : item))
                                    )
                                  }
                                  placeholder="0.00"
                                  inputClassName="text-right"
                                />
                              </div>
                            </div>
                          ) : (
                            <span className="font-medium">
                              {formatMoney(
                                {
                                  amount: niOriginalGross,
                                  currencyCode: order.currencyCode,
                                  currencySymbol: order.currencySymbol
                                },
                                locale
                              )}
                            </span>
                          )}
                        </TableCell>

                        {/* Discount Rate */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {isEditingNew ? (
                            <div className="ml-auto w-24">
                              <NumberInput
                                min={0}
                                max={100}
                                stepper={0.5}
                                decimalScale={2}
                                suffix="%"
                                value={ni.discountRateOverride}
                                onValueChange={v =>
                                  setPendingNewItems(prev =>
                                    prev.map((item, i) => (i === idx ? { ...item, discountRateOverride: v } : item))
                                  )
                                }
                                placeholder="0"
                                inputClassName="text-right"
                              />
                            </div>
                          ) : niDiscountRate > 0 ? (
                            <Badge variant="outline" className="text-xs">
                              %{niDiscountRate}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Discounted Price */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="font-medium">
                            {formatMoney(
                              {
                                amount: unitGross,
                                currencyCode: order.currencyCode,
                                currencySymbol: order.currencySymbol
                              },
                              locale
                            )}
                          </div>
                          {niDiscountAmount > 0 && (
                            <div className="text-xs text-green-600">
                              -
                              {formatMoney(
                                {
                                  amount: niDiscountAmount,
                                  currencyCode: order.currencyCode,
                                  currencySymbol: order.currencySymbol
                                },
                                locale
                              )}
                            </div>
                          )}
                        </TableCell>

                        {/* Price without Tax */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {formatMoney(
                            {
                              amount: niUnitNet,
                              currencyCode: order.currencyCode,
                              currencySymbol: order.currencySymbol
                            },
                            locale
                          )}
                        </TableCell>

                        {/* Price with Tax */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {formatMoney(
                            {
                              amount: unitGross,
                              currencyCode: order.currencyCode,
                              currencySymbol: order.currencySymbol
                            },
                            locale
                          )}
                        </TableCell>

                        {/* Tax Rate */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          {isEditingNew ? (
                            <div className="ml-auto w-24">
                              <NumberInput
                                min={0}
                                max={100}
                                stepper={0.5}
                                decimalScale={2}
                                suffix="%"
                                value={ni.taxRate}
                                onValueChange={v =>
                                  setPendingNewItems(prev =>
                                    prev.map((item, i) => (i === idx ? { ...item, taxRate: v } : item))
                                  )
                                }
                                placeholder="0"
                                inputClassName="text-right"
                              />
                            </div>
                          ) : (ni.taxRate ?? 0) > 0 ? (
                            <Badge variant="outline" className="text-xs">
                              %{ni.taxRate}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Price includes tax badge */}
                        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                          {ni.priceIncludesTax !== null ? (
                            <Badge variant={ni.priceIncludesTax ? 'default' : 'secondary'} className="text-xs">
                              {ni.priceIncludesTax ? t('incl_tax') : t('excl_tax')}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Subtotal */}
                        <TableCell className="text-right font-medium">
                          {formatMoney(
                            {
                              amount: lineTotal,
                              currencyCode: order.currencyCode,
                              currencySymbol: order.currencySymbol
                            },
                            locale
                          )}
                        </TableCell>
                        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                          {isEditingNew ? (
                            <div className="flex justify-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                title={t('save')}
                                onClick={() => setEditingNewIdx(null)}>
                                <SaveIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-center gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => setEditingNewIdx(idx)}>
                                <PencilIcon className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive h-7 w-7"
                                onClick={() => {
                                  setPendingNewItems(prev => prev.filter((_, i) => i !== idx))
                                  if (editingNewIdx === idx) {
                                    setEditingNewIdx(null)
                                  }
                                }}>
                                <Trash2Icon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="mt-4 flex flex-col items-end gap-2">
              <div className="flex min-w-56 items-center justify-between gap-8">
                <span className="text-muted-foreground text-sm">{t('item_count')}</span>
                <span className="font-medium">
                  {(() => {
                    const activeItems = order.items.filter(i => !pendingDeleteItemIds.has(i.id))
                    const totalQty =
                      activeItems.reduce((s, i) => s + (pendingItemEdits[i.id]?.qty ?? i.quantity), 0) +
                      pendingNewItems.reduce((s, ni) => s + ni.quantity, 0)
                    const lineCount = activeItems.length + pendingNewItems.length
                    return `${totalQty} (${lineCount} ${t('line')})`
                  })()}
                </span>
              </div>
              <div className="flex min-w-56 items-center justify-between gap-8">
                <span className="text-muted-foreground text-sm">{t('subtotal')}</span>
                <span className={`font-medium ${showPreviewTotals ? 'text-amber-600' : ''}`}>
                  {formatMoney(
                    {
                      amount: previewTotals.subtotal,
                      currencyCode: order.currencyCode,
                      currencySymbol: order.currencySymbol
                    },
                    locale
                  )}
                </span>
              </div>

              {/* Discount rate */}
              <div className="flex min-w-56 items-center justify-between gap-8">
                <span className="text-muted-foreground text-sm">{t('discount_rate')}</span>
                {editingDiscount ? (
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground text-sm">%</span>
                    <div className="w-20">
                      <NumberInput
                        min={0}
                        max={100}
                        stepper={1}
                        decimalScale={2}
                        value={pendingDiscountRate ?? effectiveDiscountRate}
                        onValueChange={v => setPendingDiscountRate(v ?? 0)}
                        onKeyDown={e => e.key === 'Enter' && setEditingDiscount(false)}
                        inputClassName="text-right"
                      />
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingDiscount(false)}>
                      <SaveIcon className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => {
                        setEditingDiscount(false)
                        setPendingDiscountRate(undefined)
                      }}>
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <span
                      className={`font-medium ${pendingDiscountRate !== undefined && pendingDiscountRate !== order.customer.discountRate ? 'text-amber-600' : ''}`}>
                      %{effectiveDiscountRate}
                    </span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={() => {
                        setPendingDiscountRate(effectiveDiscountRate)
                        setEditingDiscount(true)
                      }}>
                      <PencilIcon className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              {previewTotals.discountAmount > 0 && (
                <div className="flex min-w-56 items-center justify-between gap-8">
                  <span className="text-muted-foreground text-sm">{t('discount_amount')}</span>
                  <span className={`text-destructive font-medium ${showPreviewTotals ? 'text-amber-600' : ''}`}>
                    -
                    {formatMoney(
                      {
                        amount: previewTotals.discountAmount,
                        currencyCode: order.currencyCode,
                        currencySymbol: order.currencySymbol
                      },
                      locale
                    )}
                  </span>
                </div>
              )}
              {previewTotals.taxTotal > 0 && (
                <div className="flex min-w-56 items-center justify-between gap-8">
                  <span className="text-muted-foreground text-sm">{t('tax_total')}</span>
                  <span className={`font-medium ${showPreviewTotals ? 'text-amber-600' : ''}`}>
                    {formatMoney(
                      {
                        amount: previewTotals.taxTotal,
                        currencyCode: order.currencyCode,
                        currencySymbol: order.currencySymbol
                      },
                      locale
                    )}
                  </span>
                </div>
              )}
              <Separator className="w-56" />
              <div className="flex min-w-56 items-center justify-between gap-8">
                <span className="font-semibold">{t('total_amount')}</span>
                <span className={`text-lg font-bold ${showPreviewTotals ? 'text-amber-600' : ''}`}>
                  {formatMoney(
                    {
                      amount: previewTotals.total,
                      currencyCode: order.currencyCode,
                      currencySymbol: order.currencySymbol
                    },
                    locale
                  )}
                </span>
              </div>
              {showPreviewTotals && <p className="text-muted-foreground text-xs">{t('preview_unsaved')}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <StickyNoteIcon className="h-4 w-4" />
              {t('notes')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {order.notes.map(note => (
              <div key={note.id} className="space-y-2 rounded-lg border p-3">
                {editingNoteId === note.id ? (
                  <>
                    <Textarea value={editNoteContent} onChange={e => setEditNoteContent(e.target.value)} rows={3} />
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`note-public-edit-${note.id}`}
                        checked={editNoteIsPublic}
                        onCheckedChange={setEditNoteIsPublic}
                      />
                      <Label htmlFor={`note-public-edit-${note.id}`} className="text-sm">
                        {t('visible_to_customer')}
                      </Label>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={saveEditNote} disabled={isUpdatingNote}>
                        <SaveIcon className="h-4 w-4" />
                        {t('save')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingNoteId(null)}>
                        <XIcon className="h-4 w-4" />
                        {t('cancel')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm">{note.content}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant={note.isPublic ? 'default' : 'secondary'} className="gap-1 text-xs">
                          {note.isPublic ? <EyeIcon className="h-3 w-3" /> : <EyeOffIcon className="h-3 w-3" />}
                          {note.isPublic ? t('visible_to_customer') : t('admin_only')}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {note.userEmail} · {formatDateTime(note.createdAt, locale)}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEditNote(note)}>
                          <PencilIcon className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive h-7 w-7"
                          onClick={() => setDeleteNoteId(note.id)}>
                          <Trash2Icon className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
            <Separator />
            <div className="space-y-3">
              <p className="text-sm font-medium">{t('add_note')}</p>
              <Textarea
                placeholder={t('note_placeholder')}
                value={noteContent}
                onChange={e => setNoteContent(e.target.value)}
                rows={3}
              />
              <div className="flex items-center gap-2">
                <Switch id="note-public" checked={noteIsPublic} onCheckedChange={setNoteIsPublic} />
                <Label htmlFor="note-public" className="text-sm">
                  {t('visible_to_customer')}
                </Label>
              </div>
              <Button size="sm" onClick={handleAddNote} disabled={!noteContent.trim() || isAddingNote}>
                <PlusIcon className="h-4 w-4" />
                {t('add_note')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Change Log */}
        {order.changeLogs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ClockIcon className="h-4 w-4" />
                {t('change_log')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.changeLogs.map(log => (
                  <div key={log.id} className="flex items-start gap-3 text-sm">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-neutral-300" />
                    <div className="min-w-0 flex-1">
                      <p>{log.description}</p>
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        {log.userId ? (
                          <Link href={`/users/${log.userId}`} className="hover:underline">
                            {log.userEmail}
                          </Link>
                        ) : (
                          log.userEmail
                        )}
                        {' · '}
                        {formatDateTime(log.createdAt, locale)}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-xs">
                      {t(`order_change_type_${log.changeType}` as any)}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <DeleteConfirmationDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />

      <Dialog
        open={statusDialogOpen}
        onOpenChange={open => {
          if (!isUpdating) {
            setStatusDialogOpen(open)
          }
        }}>
        <DialogContent className="sm:max-w-sm" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('update_order_status')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="dialog-status">{t('status')}</Label>
              <Select
                value={selectedStatus ?? order.status}
                onValueChange={val => setSelectedStatus(val as OrderStatusEnum)}>
                <SelectTrigger id="dialog-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(OrderStatusEnum).map(status => (
                    <SelectItem key={status} value={status}>
                      {t(`order_status_${status}` as any)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dialog-locale">{t('language')}</Label>
              <Select
                value={pendingDialogLocale ?? order.locale ?? ''}
                onValueChange={val => setPendingDialogLocale(val)}>
                <SelectTrigger id="dialog-locale" className="w-full">
                  <SelectValue placeholder={order.locale ?? '—'} />
                </SelectTrigger>
                <SelectContent>
                  {activeLocales.map(loc => (
                    <SelectItem key={loc.fullCode} value={loc.fullCode}>
                      {loc.name} ({loc.fullCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {order.customerType === 'guest' && (
              <div className="flex items-center gap-3">
                <Switch
                  id="dialog-show-prices"
                  checked={pendingDialogShowPrices !== undefined ? pendingDialogShowPrices : order.showPrices}
                  onCheckedChange={val => setPendingDialogShowPrices(val)}
                />
                <Label htmlFor="dialog-show-prices" className="cursor-pointer">
                  {t('show_prices_to_customer')}
                </Label>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Switch id="notify-customer" checked={notifyCustomer} onCheckedChange={setNotifyCustomer} />
              <Label htmlFor="notify-customer" className="cursor-pointer">
                {t('notify_customer_by_email')}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} disabled={isUpdating}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleStatusUpdate}
              disabled={
                isUpdating ||
                ((selectedStatus === null || selectedStatus === order.status) &&
                  (pendingDialogShowPrices === undefined || pendingDialogShowPrices === order.showPrices) &&
                  (pendingDialogLocale || '') === (order.locale ?? ''))
              }>
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={!!deleteNoteId}
        onCancel={() => setDeleteNoteId(null)}
        onConfirm={confirmDeleteNote}
        isDeleting={isDeletingNote}
      />

      <UnsavedChangesGuardDialog open={isNavigationBlocked} onConfirm={confirmNavigation} onCancel={cancelNavigation} />

      <StickyBottomSaveBar
        isDirty={isDirty}
        isSaving={isSavingAll}
        onSave={handleSaveAll}
        onDiscard={handleDiscardAll}
      />
    </>
  )
}

export default Detail
